import React, { ChangeEvent, FormEvent, useCallback, useState } from "react";
import { SmartCaptcha } from "@yandex/smart-captcha";
import Button from "../Button/Button";
import { useApplicationAPI } from "../../hooks/api/use-ApplicationAPI";
import {
  Application,
  ButtonContainer,
  CaptchaContainer,
  ConfigurationMessage,
  ConsentLabel,
  FieldControl,
  HeaderContainer,
  InputColumn,
  OuterContainer,
  StatusMessage,
  StyledCheckbox,
  TextareaControl,
} from "./ApplicationForm.styled";

interface FormValues {
  name: string;
  company: string;
  email: string;
  comment: string;
  consentAccepted: boolean;
}

type FormStatus =
  | { type: "idle"; message: "" }
  | { type: "success" | "error"; message: string };

const initialValues: FormValues = {
  name: "",
  company: "",
  email: "",
  comment: "",
  consentAccepted: false,
};

const captchaSiteKey = process.env.REACT_APP_SMARTCAPTCHA_SITE_KEY?.trim();

const ApplicationForm: React.FC = () => {
  const { submitApplication, isLoading } = useApplicationAPI();
  const [values, setValues] = useState<FormValues>(initialValues);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [status, setStatus] = useState<FormStatus>({
    type: "idle",
    message: "",
  });

  const handleFieldChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
  };

  const resetCaptcha = useCallback(() => {
    setCaptchaToken("");
    setCaptchaResetKey((current) => current + 1);
  }, []);

  const sendApplication = useCallback(
    async (token: string) => {
      try {
        await submitApplication({
          ...values,
          captchaToken: token,
        });

        setValues(initialValues);
        setShowCaptcha(false);
        setStatus({
          type: "success",
          message: "Заявка отправлена. Мы свяжемся с вами в ближайшее время.",
        });
      } catch (error) {
        setStatus({
          type: "error",
          message:
            error instanceof Error
              ? error.message
              : "Не удалось отправить заявку. Попробуйте ещё раз.",
        });
      } finally {
        resetCaptcha();
      }
    },
    [resetCaptcha, submitApplication, values]
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isLoading) {
      return;
    }

    if (!values.consentAccepted) {
      setStatus({
        type: "error",
        message: "Подтвердите согласие на обработку персональных данных.",
      });
      return;
    }

    setShowCaptcha(true);

    if (!captchaSiteKey) {
      setStatus({ type: "idle", message: "" });
      return;
    }

    if (!captchaToken) {
      setStatus({ type: "idle", message: "" });
      return;
    }

    void sendApplication(captchaToken);
  };

  const handleCaptchaSuccess = (token: string) => {
    setCaptchaToken(token);
    setStatus({ type: "idle", message: "" });
    void sendApplication(token);
  };

  const handleCaptchaError = () => {
    setCaptchaToken("");
    setStatus({
      type: "error",
      message:
        "SmartCaptcha недоступна. Обновите страницу и попробуйте ещё раз.",
    });
  };

  return (
    <OuterContainer>
      <HeaderContainer>
        <h2>Оставьте заявку</h2>
        <p>Заполните небольшую форму внизу и мы обязательно с Вами свяжемся</p>
      </HeaderContainer>

      <Application onSubmit={handleSubmit}>
        <InputColumn>
          <label htmlFor="application-name">Ваше имя</label>
          <FieldControl
            id="application-name"
            name="name"
            type="text"
            value={values.name}
            onChange={handleFieldChange}
            autoComplete="name"
            required
            disabled={isLoading}
          />
        </InputColumn>

        <InputColumn>
          <label htmlFor="application-company">Компания</label>
          <FieldControl
            id="application-company"
            name="company"
            type="text"
            value={values.company}
            onChange={handleFieldChange}
            autoComplete="organization"
            disabled={isLoading}
          />
        </InputColumn>

        <InputColumn>
          <label htmlFor="application-email">Почта</label>
          <FieldControl
            id="application-email"
            name="email"
            type="email"
            value={values.email}
            onChange={handleFieldChange}
            autoComplete="email"
            required
            disabled={isLoading}
          />
        </InputColumn>

        <InputColumn>
          <label htmlFor="application-comment">Комментарий</label>
          <TextareaControl
            id="application-comment"
            name="comment"
            value={values.comment}
            onChange={handleFieldChange}
            rows={5}
            disabled={isLoading}
          />
        </InputColumn>

        <ConsentLabel htmlFor="application-consent">
          <StyledCheckbox
            id="application-consent"
            name="consentAccepted"
            type="checkbox"
            checked={values.consentAccepted}
            onChange={(event) =>
              setValues((current) => ({
                ...current,
                consentAccepted: event.target.checked,
              }))
            }
            required
            disabled={isLoading}
          />
          <span>Даю согласие на обработку персональных данных</span>
        </ConsentLabel>

        {showCaptcha && (
          <CaptchaContainer aria-live="polite">
            {captchaSiteKey ? (
              <SmartCaptcha
                key={captchaResetKey}
                sitekey={captchaSiteKey}
                language="ru"
                onSuccess={handleCaptchaSuccess}
                onTokenExpired={() => setCaptchaToken("")}
                onNetworkError={handleCaptchaError}
                onJavascriptError={handleCaptchaError}
              />
            ) : (
              <ConfigurationMessage role="alert">
                Не задан клиентский ключ SmartCaptcha.
              </ConfigurationMessage>
            )}
          </CaptchaContainer>
        )}

        {status.type !== "idle" && (
          <StatusMessage
            $type={status.type}
            role={status.type === "error" ? "alert" : "status"}
            aria-live="polite"
          >
            {status.message}
          </StatusMessage>
        )}

        <ButtonContainer>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Отправляем…" : "Отправить заявку"}
          </Button>
        </ButtonContainer>
      </Application>
    </OuterContainer>
  );
};

export default ApplicationForm;
